import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

export type ImageGroup = {
  applicationId: number;
  projectNumber: string;
  title: string;
  images: ImageInfo[];
};

export type ImageInfo = {
  attachmentId: number;
  fileName: string;
  contentType: string;
  contentLength: number;
};

export const ImageGalleryDialog: React.FC<{
  applicationIds: number[];
  label: string;
  noItemsMessage: string;
  onClose: () => void;
}> = ({ applicationIds, label, noItemsMessage, onClose }) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [groups, setGroups] = useState<ImageGroup[]>([]);

  useEffect(() => {
    dialogRef.current.showModal();

    const abortController = new AbortController();
    fetch('/api/image-gallery', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(applicationIds),
      signal: abortController.signal
    })
      .then(r => r.json())
      .then(data => setGroups(data))
      .catch(e => { if (e.name !== 'AbortError') throw e; })
      .finally(() => setIsLoading(false));

    return () => abortController.abort();
  }, []);

  const [isDownloading, setIsDownloading] = useState(false);

  const downloadZip = async () => {
    setIsDownloading(true);
    const response = await fetch('/api/image-gallery/download-zip', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(applicationIds)
    });

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Images.zip';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    setIsDownloading(false);
  };

  const hasImages = groups.length > 0;

  return (
    ReactDOM.createPortal(
      <dialog ref={dialogRef} className="modal image-gallery" style={{ width: '95vw' }} aria-labelledby="imageGalleryTitle" onClose={onClose}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h1 className="modal-title" id="imageGalleryTitle">{label}</h1>
              {hasImages && (
                <button type="button" className="btn btn-outline-primary btn-sm ms-2" onClick={downloadZip} disabled={isDownloading}>
                  {isDownloading ? (
                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  ) : (
                    <i className="fas fa-file-archive me-1"></i>
                  )}
                  Download ZIP
                </button>
              )}
              <button type="button" className="btn-close" aria-label="Close" onClick={onClose}></button>
            </div>
            <div className="modal-body" style={{ maxHeight: '85vh', overflowY: 'auto' }}>
              {isLoading ? (
                <div className="text-center py-5">
                  <span className="spinner-border" role="status" aria-hidden="true"></span>
                </div>
              ) : !hasImages ? (
                <p className="text-muted">{noItemsMessage}</p>
              ) : (
                groups.map((group) => (
                  <div key={group.applicationId} className="mb-4">
                    <h5>{group.projectNumber} - {group.title}</h5>
                    <div className="row g-3">
                      {group.images.map((img) => (
                        <div key={img.attachmentId} className="col-6 col-md-4 col-lg-3">
                          <div className="card h-100">
                            <img
                              src={`/api/image-gallery/thumbnail/${img.attachmentId}`}
                              alt={img.fileName}
                              className="card-img-top"
                              style={{ objectFit: 'contain', height: '200px' }}
                            />
                            <div className="card-body p-2">
                              <a
                                href={`/api/image-gallery/download/${img.attachmentId}`}
                                className="btn btn-outline-primary btn-sm w-100 text-truncate"
                                title={img.fileName}
                              >
                                <i className="fas fa-download me-1"></i>
                                {img.fileName}
                              </a>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </dialog>,
      document.body
    )
  );
};
